import { displayName, hasJoined } from "../../../../engine/selectors";
import { unlock, whistle } from "../../lib/buzzer";
import { clockTime, type TabProps } from "./common";
import { dayCsv, dayStamp, download } from "./exportDay";
import { Fold } from "./Fold";
import { Pace, ScreensStrip, WallMessage } from "./Hall";
import { shortLink } from "../../../../shared/short";
import { LinkRow, Qr, absolute } from "./Links";

const mins = (ms: number): string => `${Math.round((ms / 60_000) * 100) / 100} min`;

/**
 * The hall right now (screens, a message for the wall, the pace). Then, each in a fold: is
 * everything ready, the links the hall needs, and taking the day home.
 */
export function DayTab(props: TabProps) {
  const { t, state, say } = props;
  const players = Object.values(state.players);
  const here = players.filter((p) => p.status === "in").length;
  const notJoined = players.filter((p) => !hasJoined(p)).map((p) => displayName(p, "admin")).sort((x, y) => x.localeCompare(y));
  const { lastWhistleAt, gameMs, changeoverMs } = state.config;
  const links = t.links;

  function testSound() {
    try {
      unlock();
      whistle();
      say("Whistle played. Heard nothing? Turn this phone's sound up.");
    } catch {
      say("This phone could not play the whistle.");
    }
  }

  const stamp = dayStamp(t.now());

  return (
    <div className="ad-tab">
      <ScreensStrip {...props} />

      <WallMessage {...props} />

      <Pace {...props} />

      <Fold id="day.before" title="Before the day" summary={`${here} of ${players.length} here, ${players.length - notJoined.length} joined`}>
        <dl className="ad-facts">
          <div><dt>Players</dt><dd>{players.length} on the list, {here} here</dd></div>
          <div>
            <dt>Joined</dt>
            <dd>
              {players.length - notJoined.length} of {players.length}
              {notJoined.length ? `. Not yet: ${notJoined.join(", ")}.` : ". Everyone has a picture or has been let in."}
            </dd>
          </div>
          <div><dt>Last whistle</dt><dd>{lastWhistleAt !== null ? clockTime(lastWhistleAt) : "Not set. Rounds tab, Settings for the day."}</dd></div>
          <div><dt>Slot</dt><dd>Game {mins(gameMs)}, changeover {mins(changeoverMs)}</dd></div>
        </dl>
        <h3>Projector</h3>
        {links?.projector || links?.calibration ? (
          <div className="ad-grid2">
            {links.projector && <a className="btn quiet" href={absolute(links.projector)} target="_blank" rel="noreferrer">Open the projector</a>}
            {links.calibration && <a className="btn quiet" href={absolute(links.calibration)} target="_blank" rel="noreferrer">Open the calibration</a>}
          </div>
        ) : (
          <NoLinks t={t} />
        )}
      </Fold>

      <Fold id="day.courts" title="Court tablets" summary={links?.courts.length ? `${links.courts.length} links` : "no links yet"}>
        {links?.courts.length ? (
          <ul className="ad-list ad-links">
            {links.courts.map((l, i) => <LinkRow key={l} label={`Court ${i + 1}`} link={shortLink(l, i + 1, location.origin)} say={say} />)}
          </ul>
        ) : (
          <NoLinks t={t} />
        )}
      </Fold>

      <Fold id="day.qr" title="Players' QR" summary="if the projector is late">
        {links?.players ? (
          <>
            <p className="ad-note">Show this if the projector is late. It opens the players' page.</p>
            <Qr url={shortLink(links.players, "players", location.origin)} big />
            <div className="ad-link-url">{shortLink(links.players, "players", location.origin)}</div>
          </>
        ) : (
          <NoLinks t={t} />
        )}
      </Fold>

      <Fold id="day.sound" title="Test the sound" summary="this phone only">
        <p className="ad-note">Plays the whistle on this phone.</p>
        <button type="button" className="btn ad-wide" onClick={testSound}>Play the whistle</button>
      </Fold>

      <Fold id="day.export" title="Export the day" summary="two files">
        <p className="ad-note">Every game, then both boards. Take one at the end, before any reset.</p>
        <div className="ad-grid2">
          <button type="button" className="btn quiet"
            onClick={() => download(`bdayminton-${stamp}.json`, "application/json", JSON.stringify(state, null, 2))}>
            Download JSON
          </button>
          <button type="button" className="btn quiet"
            onClick={() => download(`bdayminton-${stamp}.csv`, "text/csv", dayCsv(state))}>
            Download CSV
          </button>
        </div>
      </Fold>
    </div>
  );
}

function NoLinks({ t }: { t: TabProps["t"] }) {
  return (
    <>
      <p className="ad-note">The links have not arrived yet.</p>
      <button type="button" className="btn quiet ad-wide" onClick={() => t.askLinks()}>Ask for the links</button>
    </>
  );
}
